import React, {useMemo} from "react";
import Box from "@material-ui/core/Box";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import {grey} from "@material-ui/core/colors";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import LockIcon from "@material-ui/icons/Lock";
import CopyAllIcon from "view/images/CopyAllIcon";
import PinIcon from "view/images/PinIcon";
import ClipboardButton from "view/components/ClipboardButton";
import {generatePath} from "react-router-dom";
import {useCurrentShowBuildTypes, useCurrentShowId} from "hooks";
import {SHOW_INFO_PATH} from "view/routes";
import {ICategory, ITerm} from "@evennt-tech/api-v2-core";
import {useTheme} from "@material-ui/core";
import {glossaryStore} from "store/glossary/glossary.store";
import {isLinkedTerm} from "api/showInfoLibrary/types";
import Button from "@material-ui/core/Button";
import FlexBox from "view/components/Layout/FlexBox";
import {getItemById} from "tools/common/getItemById";
import clsx from "clsx";
import useStyles from "./styles";

export type Actions = {
  /*
   * Absent on a linked section for a non-admin — see `canOpenSectionForm`. The
   * button is then disabled rather than dropped, so the lock still explains
   * itself.
   */
  onEdit?: () => void;
  onRemove: () => void;
  onPin: () => void;
  onPopup: () => void;
  onMoveDown: (term: ITerm) => void;
  onMoveUp: (term: ITerm) => void;
};

export type GlossaryActionsProps = {
  term: ITerm;
  terms: ITerm[];
  actions?: Actions;
  categories: ICategory[];
  isPrintView?: boolean;
};

const GlossaryTermActions: React.FC<GlossaryActionsProps> = ({
  term,
  actions,
  terms,
  categories,
  isPrintView,
}) => {
  const classes = useStyles({interactive: false});
  const theme = useTheme();
  const showId = useCurrentShowId();
  const [buildTypes] = useCurrentShowBuildTypes();
  const popupTermId = glossaryStore((state) => {
    return state.popupTermId[showId] ?? null;
  });

  const termsToBuildersWithPopup = useMemo(() => {
    if (!term.termToBuilders) {
      return [];
    }
    return term.termToBuilders.filter((ttb) => ttb.shouldShowPopup);
  }, [term.termToBuilders]);

  const buildTypeById = useMemo(() => getItemById(buildTypes ?? []), [
    buildTypes,
  ]);

  const renderActions = () => {
    if (!actions || isPrintView) {
      return null;
    }

    const {onEdit, onRemove, onMoveUp, onMoveDown} = actions;

    // Three states: local, linked-and-mine, linked-and-not-mine.
    const editTooltip = (() => {
      if (!isLinkedTerm(term)) return "Edit";

      return onEdit
        ? "Managed in the library: the title and text are changed there, or after unlinking this section."
        : "The section is managed in the library and can only be edited by an Organiser Admin.";
    })();

    const onClickRemove = () => {
      if (confirm("Are you sure you want to delete this show info section?")) {
        onRemove();
      }
    };

    const firstUnpinnedItemIndex = terms.findIndex(
      (curTerm) => !curTerm.pinned
    );
    const lastGlossaryItem = terms[terms.length - 1];

    const category = categories.find(
      (category) => category.id === term.categoryId
    );
    if (!category) {
      return;
    }
    const termUrl = `${window.location.origin}${generatePath(SHOW_INFO_PATH, {
      showId,
    })}?category=${encodeURIComponent(category?.id)}&termId=${term.id}`;

    return (
      <>
        <ClipboardButton
          className={classes.action}
          value={termUrl}
          copyIconColor={grey[600]}
        />
        {!term.pinned && term?.id !== lastGlossaryItem?.id && (
          <IconButton
            className={classes.action}
            onClick={async () => onMoveDown(term)}
          >
            <ArrowDownwardIcon />
          </IconButton>
        )}
        {!term.pinned && term?.id !== terms[firstUnpinnedItemIndex]?.id && (
          <IconButton
            className={classes.action}
            onClick={async () => onMoveUp(term)}
          >
            <ArrowUpwardIcon />
          </IconButton>
        )}
        {/*
          The lock says which fields the form will not let go of: the title and
          text are the library's. The span is not decoration — a disabled button
          fires no mouse events, so the tooltip needs something live.
        */}
        <Tooltip title={editTooltip} aria-label="edit">
          <span className={classes.actionWrap}>
            <IconButton
              onClick={onEdit}
              disabled={!onEdit}
              className={classes.action}
              aria-label="Edit"
            >
              {isLinkedTerm(term) ? (
                <Box className={classes.lockedEdit}>
                  <EditIcon />
                  <LockIcon className={classes.lockedEditBadge} />
                </Box>
              ) : (
                <EditIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove" aria-label="remove">
          <IconButton
            onClick={onClickRemove}
            className={classes.action}
            aria-label="Remove"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  const renderPin = () => {
    const {pinned} = term;

    if (!actions) {
      return pinned && <PinIcon color={theme.palette.primary.main} />;
    }

    return (
      <Tooltip
        title={pinned ? "Unpin" : "Pin"}
        aria-label={pinned ? "unpin" : "pin"}
      >
        <IconButton
          onClick={actions.onPin}
          className={`${classes.action} ${pinned && classes.pin}`}
          aria-label="Pin"
        >
          <PinIcon color={pinned ? theme.palette.primary.main : grey[600]} />
        </IconButton>
      </Tooltip>
    );
  };

  const popupBuildTypes = useMemo(() => {
    return termsToBuildersWithPopup.reduce<string[]>((acc, curr) => {
      const buildTypeName = buildTypeById[curr.builderId]?.name;
      if (buildTypeName) {
        acc.push(buildTypeName);
      }

      return acc;
    }, []);
  }, [termsToBuildersWithPopup, buildTypeById]);

  const getPopupBuildTypesLabel = () => {
    const termsToBuildersLen = termsToBuildersWithPopup.length;
    if (!termsToBuildersLen) {
      return "";
    }
    if (termsToBuildersLen === buildTypes.length) {
      return "All Build Types";
    }

    return `${termsToBuildersLen} Build Type${
      termsToBuildersLen > 1 ? "s" : ""
    }`;
  };

  const renderPopup = () => {
    if (isPrintView) {
      return null;
    }

    const isPopupExhibitor = popupTermId === term.id;
    if (!actions) {
      return (
        isPopupExhibitor && (
          <CopyAllIcon
            color={theme.palette.primary.main}
            className={classes.noPrint}
          />
        )
      );
    }

    const shouldShowTooltip =
      termsToBuildersWithPopup.length > 0 &&
      termsToBuildersWithPopup.length < buildTypes.length;

    const tooltipTitle = shouldShowTooltip ? popupBuildTypes.join("\n") : "";

    return (
      <Tooltip
        title={tooltipTitle}
        disableHoverListener={!shouldShowTooltip}
        classes={{tooltip: classes.popupTooltip}}
      >
        <Button
          onClick={actions.onPopup}
          className={clsx(classes.popupAction, classes.noPrint)}
          style={shouldShowTooltip ? {textDecoration: "underline"} : undefined}
        >
          <FlexBox gap={1} alignCenter>
            <CopyAllIcon
              color={
                termsToBuildersWithPopup.length
                  ? theme.palette.primary.main
                  : grey[600]
              }
            />
            {getPopupBuildTypesLabel()}
          </FlexBox>
        </Button>
      </Tooltip>
    );
  };

  return (
    <Box className={classes.actions}>
      {renderActions()}
      {renderPin()}
      {renderPopup()}
    </Box>
  );
};

export default GlossaryTermActions;
